import React from 'react';

import { A11YLiveMessage } from '@lumapps/aria-live/components/A11YLiveMessage';
import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Form } from '@lumapps/lumx-forms/components/Form';
import { FormTextField } from '@lumapps/lumx-forms/components/FormTextField';
import { mdiClose } from '@lumapps/lumx/icons';
import { Alignment, Emphasis, FlexBox, IconButton, Message, Orientation, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { FEEDBACK_COMMENT_MAX_LENGTH } from '../../constants';
import { AI } from '../../keys';
import './index.scss';
import { AiDetailedFeedback, AiFeedbackTypes } from '../../types';
import { AiFeedbackReasonOptions } from './AiFeedbackChips';

const CLASSNAME = 'feedback-reasons';

export interface AiFeedbackReasonsProps {
    isOpen: boolean;
    onClose: () => void;
    sendFeedback: (feedback: AiDetailedFeedback) => void;
    /** Current feedback type - used to reset state when user switches feedback */
    feedbackType?: 'like' | 'dislike';
    /** Whether to show the feedback reason options (chip selector) */
    showReasonOptions?: boolean;
    /** Whether to show the comment field */
    showCommentField?: boolean;
    /** Maximum character length for the comment field */
    maxCommentLength?: number;
}

export const AiFeedbackReasons = ({
    isOpen,
    onClose,
    sendFeedback,
    feedbackType,
    showReasonOptions = true,
    showCommentField = true,
    maxCommentLength = FEEDBACK_COMMENT_MAX_LENGTH,
}: AiFeedbackReasonsProps) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(CLASSNAME);

    const [isFeedbackSent, setIsFeedbackSent] = React.useState(false);

    // Reset when feedback type changes (user switched feedback)
    React.useEffect(() => {
        setIsFeedbackSent(false);
    }, [feedbackType]);

    const sendDetailedFeedback = (feedback?: AiDetailedFeedback) => {
        setIsFeedbackSent(true);
        onClose();

        if (feedback) {
            sendFeedback(feedback);
        }
    };

    if (!showCommentField && !showReasonOptions) {
        return null;
    }

    return (
        <A11YLiveMessage className={block()}>
            {isOpen && (
                <FlexBox className={element('choices')} orientation={Orientation.vertical} gap={Size.big}>
                    <Form
                        form={{
                            values: {
                                type: AiFeedbackTypes.DISLIKED,
                                reason: '',
                            },
                        }}
                        onSubmit={sendDetailedFeedback}
                        formActionProps={{
                            saveProps: {
                                emphasis: Emphasis.medium,
                                children: translateKey(GLOBAL.SEND),
                            },
                        }}
                    >
                        <FlexBox
                            orientation={Orientation.horizontal}
                            hAlign={Alignment.top}
                            vAlign={showReasonOptions ? Alignment.spaceBetween : Alignment.right}
                        >
                            {showReasonOptions && <AiFeedbackReasonOptions />}
                            <IconButton
                                emphasis="low"
                                size="m"
                                icon={mdiClose}
                                label={translateKey(GLOBAL.CLOSE)}
                                onClick={onClose}
                                {...get({ element: 'button', action: 'close' })}
                            />
                        </FlexBox>

                        {showCommentField && (
                            <FormTextField
                                name="comment"
                                label={translateKey(AI.HOW_CAN_ANSWER_BE_IMPROVED)}
                                placeholder={translateKey(GLOBAL.ADD_A_COMMENT)}
                                multiline
                                minimumRows={1}
                                maxLength={maxCommentLength}
                                // We want validation only if comment is available
                                isRequired={!showReasonOptions}
                                preventEmptyFields={!showReasonOptions}
                            />
                        )}
                    </Form>
                </FlexBox>
            )}

            {!isOpen && isFeedbackSent && (
                <Message kind="success" hasBackground>
                    {translateKey(AI.FEEDBACK_SENT)}
                </Message>
            )}
        </A11YLiveMessage>
    );
};
